import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Check, AlertCircle, Leaf } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_POLLS = 8;

export default function DepositSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-navy grain flex items-center justify-center px-5" data-testid="deposit-success-page">
      <div className="bg-white rounded-3xl p-10 max-w-md text-center shadow-2xl">
        {state === "checking" && (<><Loader2 className="w-12 h-12 text-leaf animate-spin mx-auto mb-5" /><h1 className="font-heading text-2xl font-semibold text-navy mb-2">Confirming your deposit…</h1><p className="text-muted-foreground">Hang tight, this only takes a moment.</p></>)}
        {state === "paid" && (<>
          <div className="w-16 h-16 rounded-full bg-leaf mx-auto flex items-center justify-center mb-5"><Check className="w-8 h-8 text-white" /></div>
          <h1 className="font-heading text-2xl font-semibold text-navy mb-2" data-testid="deposit-paid-heading">Booking confirmed!</h1>
          <p className="text-muted-foreground mb-6">Your $50 deposit is received and applied to your total. Our team will reach out to finalize your date and exact price.</p>
          <div className="flex items-center gap-2 justify-center text-leaf mb-6"><Leaf className="w-4 h-4" /><span className="text-sm font-medium">Green Cleaning included on every visit</span></div>
          <Link to="/" data-testid="deposit-home-link" className="inline-block px-7 py-3.5 rounded-full bg-leaf text-white font-medium hover:scale-105 transition-transform">Back to Home</Link>
        </>)}
        {state === "pending" && (<><Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-5" /><h1 className="font-heading text-2xl font-semibold text-navy mb-2">Almost there</h1><p className="text-muted-foreground mb-6">Your payment is processing. We've saved your booking and will confirm shortly by email.</p><Link to="/" className="inline-block px-7 py-3.5 rounded-full bg-navy text-white font-medium hover:scale-105 transition-transform">Back to Home</Link></>)}
        {state === "error" && (<><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-5" /><h1 className="font-heading text-2xl font-semibold text-navy mb-2">We couldn't confirm the deposit</h1><p className="text-muted-foreground mb-6">No worries — your booking request is saved. Please call us or try again.</p><Link to="/" className="inline-block px-7 py-3.5 rounded-full bg-navy text-white font-medium hover:scale-105 transition-transform">Back to Home</Link></>)}
      </div>
    </div>
  );
}
